import React, { useMemo } from 'react';
import { Filter, X } from 'lucide-react';

const CategoryFilter = ({ categories, selectedFilters, onFilterChange, onClear, questions }) => {
  // Filter topics based on selected subject and grade
  const availableTopics = useMemo(() => {
    if (!questions || questions.length === 0) return categories.topics;

    const filteredQuestions = questions.filter(q => {
      let matches = true;
      if (selectedFilters.subject && q.subject !== selectedFilters.subject) {
        matches = false;
      }
      if (selectedFilters.grade && q.grade !== selectedFilters.grade) {
        matches = false;
      }
      return matches;
    });

    const topicsSet = new Set();
    filteredQuestions.forEach(q => {
      if (q.topics && Array.isArray(q.topics)) {
        q.topics.forEach(topic => topicsSet.add(topic));
      }
      if (q.type === 'stem' && q.subQuestions) {
        q.subQuestions.forEach(subQ => {
          if (subQ.topics && Array.isArray(subQ.topics)) {
            subQ.topics.forEach(topic => topicsSet.add(topic));
          }
        });
      }
    });

    return Array.from(topicsSet).sort();
  }, [questions, selectedFilters.subject, selectedFilters.grade, categories.topics]);

  const handleSubjectChange = (subject) => {
    onFilterChange({
      subject: subject || null,
      grade: selectedFilters.grade,
      topics: [] // Clear topics when subject changes
    });
  };

  const handleGradeChange = (grade) => {
    onFilterChange({
      subject: selectedFilters.subject,
      grade: grade || null,
      topics: [] // Clear topics when grade changes
    });
  };

  const handleTopicToggle = (topic) => {
    const currentTopics = selectedFilters.topics || [];
    const newTopics = currentTopics.includes(topic)
      ? currentTopics.filter(t => t !== topic)
      : [...currentTopics, topic];
    
    onFilterChange({
      ...selectedFilters,
      topics: newTopics
    });
  };

  const hasActiveFilters = selectedFilters.subject || selectedFilters.grade || 
    (selectedFilters.topics && selectedFilters.topics.length > 0);

  return (
    <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-indigo-400" />
          <h3 className="text-lg font-semibold text-slate-200">Filter Questions</h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 px-3 py-1 text-sm text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-md transition"
          >
            <X className="h-4 w-4" />
            Clear
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Subject Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Subject
          </label>
          <select
            value={selectedFilters.subject || ''}
            onChange={(e) => handleSubjectChange(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Subjects</option>
            {categories.subjects.map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
        </div>

        {/* Grade Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Grade
          </label>
          <select
            value={selectedFilters.grade || ''}
            onChange={(e) => handleGradeChange(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Grades</option>
            {categories.grades.map(grade => (
              <option key={grade} value={grade}>{grade}</option>
            ))}
          </select>
        </div>

        {/* Topics Filter */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Topics
            {(selectedFilters.subject || selectedFilters.grade) && (
              <span className="text-xs text-slate-400 ml-2">
                (filtered by {[selectedFilters.subject, selectedFilters.grade].filter(Boolean).join(' & ')})
              </span>
            )}
          </label>
          <div className="max-h-32 overflow-y-auto bg-slate-700 border border-slate-600 rounded-md p-2">
            {availableTopics.length === 0 ? (
              <p className="text-sm text-slate-400">
                {selectedFilters.subject || selectedFilters.grade 
                  ? 'No topics for selected filters' 
                  : 'No topics available'}
              </p>
            ) : (
              availableTopics.map(topic => (
                <label key={topic} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-600 px-2 rounded">
                  <input
                    type="checkbox"
                    checked={(selectedFilters.topics || []).includes(topic)}
                    onChange={() => handleTopicToggle(topic)}
                    className="rounded bg-slate-600 border-slate-500 text-indigo-500 focus:ring-indigo-600"
                  />
                  <span className="text-sm text-slate-200">{topic}</span>
                </label>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <div className="flex flex-wrap gap-2">
            {selectedFilters.subject && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
                Subject: {selectedFilters.subject}
                <button
                  onClick={() => handleSubjectChange(null)}
                  className="hover:bg-indigo-700 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedFilters.grade && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
                Grade: {selectedFilters.grade}
                <button
                  onClick={() => handleGradeChange(null)}
                  className="hover:bg-indigo-700 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedFilters.topics?.map(topic => (
              <span key={topic} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
                {topic}
                <button
                  onClick={() => handleTopicToggle(topic)}
                  className="hover:bg-indigo-700 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryFilter;